import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { CocoonMark } from "@/components/CocoonMark";
import { useScreensaverPrefs } from "@/lib/screensaverPrefs";

const SAVER_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/02n0v9t6_gemini_generated_video_c99516e8.mp4";

const POEM_TITLE = "Le Cancre";
const POEM_AUTHOR = "Jacques Prévert, Paroles";
const POEM_LINES = [
  "Il dit non avec la tête",
  "Mais il dit oui avec le cœur",
  "Il dit oui à ce qu'il aime",
  "Il dit non au professeur",
  "Il est debout",
  "On le questionne",
  "Et tous les problèmes sont posés",
  "Soudain le fou rire le prend",
  "Et il efface tout",
  "Les chiffres et les mots",
  "Les dates et les noms",
  "Les phrases et les pièges",
  "Et malgré les menaces du maître",
  "Sous les huées des enfants prodiges",
  "Avec des craies de toutes les couleurs",
  "Sur le tableau noir du malheur",
  "Il dessine le visage du bonheur.",
];
const POEM = POEM_LINES.join("\n");
const TYPE_MS = 55;
const HOLD_MS = 8000;

export function ScreenSaver() {
  const { i18n } = useTranslation();
  const prefs = useScreensaverPrefs();
  const lang = (i18n.language || "fr").slice(0, 2);
  const [active, setActive] = useState(false);
  const [typed, setTyped] = useState(0);
  const [cycle, setCycle] = useState(0);
  const timerRef = useRef(null);

  const arm = useCallback(() => {
    clearTimeout(timerRef.current);
    if (!prefs.enabled) return;
    timerRef.current = setTimeout(() => setActive(true), prefs.delayMs);
  }, [prefs.enabled, prefs.delayMs]);

  useEffect(() => {
    if (!prefs.enabled) {
      setActive(false);
      clearTimeout(timerRef.current);
      return;
    }
    const handler = () => { setActive(false); arm(); };
    const evts = ["mousemove", "mousedown", "keydown", "touchstart", "wheel", "scroll"];
    evts.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    arm();
    return () => {
      evts.forEach((e) => window.removeEventListener(e, handler));
      clearTimeout(timerRef.current);
    };
  }, [arm, prefs.enabled]);

  useEffect(() => {
    if (!active) return;
    setTyped(0);
    let i = 0;
    let restart;
    const id = setInterval(() => {
      i += 1;
      if (i <= POEM.length) {
        setTyped(i);
      } else {
        clearInterval(id);
        restart = setTimeout(() => setCycle((c) => c + 1), HOLD_MS);
      }
    }, TYPE_MS);
    return () => { clearInterval(id); clearTimeout(restart); };
  }, [active, cycle]);

  const done = typed >= POEM.length;
  const shown = POEM.slice(0, typed);
  const lines = shown.split("\n");

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
          className="fixed inset-0 z-[999] saver-shell flex flex-col items-center justify-center overflow-hidden cursor-none select-none"
          data-testid="screensaver"
          aria-hidden
        >
          <video
            src={SAVER_VIDEO}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="saver-video absolute inset-0 w-full h-full object-cover"
            data-testid="screensaver-video"
          />
          <div className="saver-video-veil" />
          <div className="saver-aura" />

          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.45, 0] }}
            transition={{ delay: 2, duration: 4, repeat: Infinity, repeatDelay: 1 }}
            className="absolute top-8 text-foreground/30 text-xs tracking-[0.25em] uppercase"
          >
            {lang === "en" ? "Move to wake" : lang === "es" ? "Muévete para despertar" : lang === "nl" ? "Beweeg om te ontwaken" : "Bougez pour reprendre"}
          </motion.span>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 1.2, ease: "easeOut" }}
            className="relative flex flex-col items-center gap-3 sm:gap-6 px-6 text-center -mt-10 sm:-mt-4"
            data-testid="screensaver-poem"
          >
            <h3 className="font-display italic text-primary/90 text-xl sm:text-3xl">{POEM_TITLE}</h3>

            <div className="font-display text-foreground/85 text-sm sm:text-lg leading-[1.45] sm:leading-[1.7] min-h-[13rem] sm:min-h-[16rem]">
              {lines.map((line, i) => {
                const isLast = i === lines.length - 1;
                return (
                  <p key={i} className="min-h-[1.45em] sm:min-h-[1.7em]">
                    {line}
                    {isLast && !done && <span className="saver-caret">▌</span>}
                  </p>
                );
              })}
            </div>
          </motion.div>

          {/* Grand titre COCOON en bas — effet waouh + auteur en dessous */}
          <div className="absolute inset-x-0 bottom-0 flex flex-col items-center justify-end pointer-events-none pb-5 sm:pb-7">
            <div className="saver-title-halo" aria-hidden />
            <motion.div
              initial={{ opacity: 0, y: 60, scale: 0.85, filter: "blur(10px)" }}
              animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
              transition={{ delay: 0.6, duration: 1.6, ease: [0.2, 0.7, 0.2, 1] }}
              className="relative"
              data-testid="screensaver-title"
            >
              <img
                src="/cocoon-logo-glow.png"
                alt="COCOON MIND"
                data-testid="screensaver-logo"
                className="mx-auto mb-3 h-20 sm:h-28 w-auto select-none"
                style={{ filter: "drop-shadow(0 0 26px hsl(28 96% 56% / 0.4))" }}
              />
              <div className="saver-title font-display text-5xl sm:text-7xl lg:text-8xl leading-none">
                <CocoonMark letterClassName="saver-title-letters" withMind />
              </div>
            </motion.div>
            <AnimatePresence>
              {done && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1.4 }}
                  className="relative mt-3 text-foreground/45 text-sm italic tracking-wide"
                  data-testid="screensaver-poem-author"
                >
                  — {POEM_AUTHOR}
                </motion.p>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
